import React from 'react';
import { useTranslation } from 'react-i18next';
import { Smartphone, Video, X } from 'lucide-react';
import type { TFunction } from 'i18next';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

import { StudioDockItemDef } from './StudioDock';
import { StudioMarkSlider, StudioPill, StudioSectionHeader } from './studio-ui';
import { StudioTakes } from './use-studio-takes';
import { PhoneCameraPanel } from '../../vcam/PhoneCameraPanel';

export interface TakeRecordedHandler {
    (take: { url: string; posterUrl?: string; durationSec: number }): void;
}

/**
 * The Video panel: scenes, their keyframes, Preview / Record, and the
 * recorded take with "Use this take" when the studio was opened to make one.
 */
export const VideoTakePanel: React.FC<{
    takes: StudioTakes;
    onTakeRecorded?: TakeRecordedHandler;
    /** Closes the studio once a take is used. */
    onClose: () => void;
}> = ({ takes, onTakeRecorded, onClose }) => {
    const { t } = useTranslation();
    const {
        scenes, activeScene, setActiveSceneId, updateScene, addScene, deleteScene,
        recordableScenes, totalDuration, recording, previewing, flying, recordPct, videoResult, videoError,
        goToKeyframe, addKeyframe, previewPath, previewAll, recordVideo,
    } = takes;
    return (
        <div>
            {/* SCENES — each is a cut; Record plays them back-to-back. */}
            <StudioSectionHeader label={t('CONTENT_STUDIO.STUDIO_SCENES', 'Scenes')} value={`${recordableScenes.length || 0} · ${totalDuration}s`} />
            <div className="mb-3 flex flex-wrap gap-1.5">
                {scenes.map((sc, i) => (
                    <div key={sc.id} className={cn('inline-flex items-center overflow-hidden', sc.id === activeScene?.id ? 'bg-foreground/15' : 'bg-foreground/5')}>
                        <button
                            onClick={() => setActiveSceneId(sc.id)}
                            title={`${sc.kfs.length} kf · ${sc.duration}s`}
                            className={cn(
                                'cursor-pointer border-0 bg-transparent px-2 py-1 text-sm font-medium hover:bg-foreground/10',
                                sc.id === activeScene?.id ? 'text-foreground' : 'text-muted-foreground',
                            )}
                        >
                            {t('CONTENT_STUDIO.STUDIO_SCENE', 'Scene')} {i + 1} · {sc.kfs.length}
                        </button>
                        {scenes.length > 1 && (
                            <button onClick={() => deleteScene(sc.id)} aria-label={t('CONTENT_STUDIO.STUDIO_REMOVE_SCENE', 'Remove scene')} className="flex cursor-pointer items-center border-0 bg-transparent px-1 py-1 text-muted-foreground hover:text-destructive">
                                <X className="size-[13px]" />
                            </button>
                        )}
                    </div>
                ))}
                <StudioPill active={false} onClick={addScene}>{t('CONTENT_STUDIO.STUDIO_NEW_SCENE', '+ New scene')}</StudioPill>
            </div>
            {/* ACTIVE SCENE — its keyframes + duration. */}
            <StudioSectionHeader label={t('CONTENT_STUDIO.STUDIO_VIDEO_KEYFRAMES', 'Camera keyframes')} value={`${activeScene?.kfs.length ?? 0}`} />
            <div className="mb-2 text-[11px] leading-snug text-muted-foreground">
                {t('CONTENT_STUDIO.STUDIO_VIDEO_HINT', 'Frame a view, add it as a keyframe, repeat — Record flies the camera smoothly through them and captures the viewport as video.')}
            </div>
            {activeScene?.take ? (
                <div className="mb-3 flex items-center gap-2 text-sm text-foreground">
                    <Smartphone className="size-4 text-muted-foreground" />
                    {t('VCAM.TAKE_SUMMARY', 'Filmed with the phone · {{s}}s', { s: activeScene.duration })}
                </div>
            ) : (
            <div className="mb-3 flex flex-wrap gap-1.5">
                {(activeScene?.kfs || []).map((kf, i) => (
                    <div key={i} className="inline-flex items-center overflow-hidden bg-foreground/5">
                        <button
                            onClick={() => goToKeyframe(kf)}
                            title={`${kf.lensMm}mm`}
                            className="cursor-pointer border-0 bg-transparent px-2 py-1 text-sm font-medium text-foreground hover:bg-foreground/10"
                        >
                            {i + 1}
                        </button>
                        <button onClick={() => updateScene(activeScene.id, (s) => ({ ...s, kfs: s.kfs.filter((_, j) => j !== i) }))} aria-label={t('CONTENT_STUDIO.STUDIO_REMOVE', 'Remove')} className="flex cursor-pointer items-center border-0 bg-transparent px-1 py-1 text-muted-foreground hover:text-destructive">
                            <X className="size-[13px]" />
                        </button>
                    </div>
                ))}
                <StudioPill active={false} onClick={addKeyframe}>{t('CONTENT_STUDIO.STUDIO_ADD_KEYFRAME', '+ Add keyframe')}</StudioPill>
            </div>
            )}
            <div className="mb-3">
                <StudioSectionHeader label={t('CONTENT_STUDIO.STUDIO_DURATION', 'Duration')} value={`${activeScene?.duration ?? 6}s`} />
                {/* Free choice, 1–15s per scene — slider snaps near the common
                    marks but any whole second in between is allowed. */}
                <StudioMarkSlider
                    value={activeScene?.duration ?? 6}
                    min={1}
                    max={15}
                    marks={[2, 4, 6, 8, 10, 15].map((v) => ({ value: v }))}
                    onChange={(v) => activeScene && updateScene(activeScene.id, (sc) => ({ ...sc, duration: Math.round(v) }))}
                />
            </div>
            <div className="mb-1.5 grid grid-cols-2 gap-1.5">
                <Button
                    variant="outline"
                    onClick={previewPath}
                    disabled={!activeScene || activeScene.kfs.length < 2 || flying}
                >
                    {previewing
                        ? `${Math.round(recordPct * 100)}%`
                        : t('CONTENT_STUDIO.STUDIO_PREVIEW_PATH', 'Preview')}
                </Button>
                <Button
                    variant="outline"
                    onClick={previewAll}
                    disabled={!recordableScenes.length || flying}
                >
                    {t('CONTENT_STUDIO.STUDIO_PREVIEW_ALL', 'Preview all')}
                </Button>
            </div>
            <Button
                variant="outline"
                onClick={recordVideo}
                disabled={!recordableScenes.length || flying}
                className="w-full"
            >
                {recording
                    ? `${t('CONTENT_STUDIO.STUDIO_RECORDING', 'Recording…')} ${Math.round(recordPct * 100)}%`
                    : `${t('CONTENT_STUDIO.STUDIO_RECORD', 'Record video')}${recordableScenes.length > 1 ? ` (${recordableScenes.length})` : ''}`}
            </Button>
            {(!activeScene || activeScene.kfs.length < 2) && (
                <div className="mt-1.5 text-[11px] text-muted-foreground">
                    {t('CONTENT_STUDIO.STUDIO_NEED_TWO_KFS', 'Add at least 2 keyframes to record.')}
                </div>
            )}
            {recordableScenes.length > 1 && (
                <div className="mt-1.5 text-[11px] text-muted-foreground">
                    {t('CONTENT_STUDIO.STUDIO_SCENES_RECORD_HINT', 'All scenes are recorded back-to-back into one video — each scene change is a cut.')}
                </div>
            )}
            {videoError && (
                <div className="mt-2 text-xs text-destructive">{videoError}</div>
            )}
            {videoResult && (
                <div className="mt-3">
                    <StudioSectionHeader label={t('CONTENT_STUDIO.STUDIO_VIDEO_READY', 'Viewport video ready')} />
                    {/* Letterbox stays black whatever the theme — video chrome. */}
                    <video src={videoResult.blobUrl} controls muted loop className="block w-full" style={{ backgroundColor: '#000' }} />
                    {onTakeRecorded && (
                        <Button
                            variant="outline"
                            className="mt-2 w-full"
                            disabled={!videoResult.videoUrl}
                            onClick={() => {
                                if (!videoResult.videoUrl) return;
                                onTakeRecorded({ url: videoResult.videoUrl, posterUrl: videoResult.firstFrameUrl, durationSec: Math.max(1, videoResult.durationSec || 0) });
                                onClose();
                            }}
                        >
                            {videoResult.videoUrl ? t('CONTENT_STUDIO.STUDIO_USE_TAKE', 'Use this take') : t('CONTENT_STUDIO.STUDIO_UPLOADING_TAKE', 'Saving the take…')}
                        </Button>
                    )}
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                        <a
                            href={videoResult.blobUrl}
                            download={`viewport-video-${Date.now()}.${videoResult.ext}`}
                            className="bg-foreground/10 px-2 py-1.5 text-sm font-medium text-foreground no-underline transition-colors hover:bg-foreground/15"
                        >
                            {t('CONTENT_STUDIO.STUDIO_DOWNLOAD_VIDEO', 'Download')}
                        </a>
                        {videoResult.videoUrl && (
                            <StudioPill active={false} onClick={() => { navigator.clipboard?.writeText(videoResult.videoUrl!); }}>{t('CONTENT_STUDIO.STUDIO_COPY_VIDEO_URL', 'Copy video URL')}</StudioPill>
                        )}
                        {videoResult.firstFrameUrl && (
                            <StudioPill active={false} onClick={() => { navigator.clipboard?.writeText(videoResult.firstFrameUrl!); }}>{t('CONTENT_STUDIO.STUDIO_COPY_FRAME_URL', 'Copy frame URL')}</StudioPill>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

/**
 * The Phone and Video dock items, in that order — the same two in every 3D
 * studio, so a take is made the same way whatever the product is.
 */
export function takeDockItems(t: TFunction, takes: StudioTakes, opts: { onTakeRecorded?: TakeRecordedHandler; onClose: () => void }): StudioDockItemDef[] {
    const { scenes, totalKfs, phone, phoneRecording } = takes;
    return [
        {
            key: 'phone',
            label: t('VCAM.DOCK', 'Phone'),
            value: phone.phoneLinked ? t('VCAM.DOCK_LIVE', 'Live') : t('VCAM.DOCK_CONNECT', 'Connect'),
            icon: <Smartphone />,
            panelWidth: 300,
            panel: <PhoneCameraPanel link={phone} recording={phoneRecording} />,
        },
        {
            key: 'video',
            label: t('CONTENT_STUDIO.STUDIO_DOCK_VIDEO', 'Video'),
            value: totalKfs ? `${scenes.length} · ${totalKfs}` : t('CONTENT_STUDIO.STUDIO_ADD_SHORT', 'Add'),
            icon: <Video />,
            panelWidth: 330,
            panel: <VideoTakePanel takes={takes} onTakeRecorded={opts.onTakeRecorded} onClose={opts.onClose} />,
        },
    ];
}
